/**
 * Central role -> capability map. Must stay in sync with backend RoleEnum.
 *
 * Role naming (as stored in DB / JWT):
 *   principal   – campus principal: full campus access incl. fees
 *   admin       – system admin (formerly controller): campus-scoped, reports & analytics
 *   administrator – campus ops without fees
 *   accountant  – fee management only for assigned campus
 */

export function getPermissions(role) {

  const normalizedRole = (role || "").toLowerCase();

  const isSuperadmin = normalizedRole === "superadmin";

  // "admin" is the new name for what was previously "controller"
  // It is now campus-scoped and has full access like a principal.
  const isAdmin = normalizedRole === "admin";

  const isViewer = normalizedRole === "viewer";

  const isTeacher = normalizedRole === "teacher";
  const isParent = normalizedRole === "parent";

  // "principal" is the new name for what was previously "admin"
  const isPrincipal = normalizedRole === "principal";

  const isAdministrator = normalizedRole === "administrator";
  const isAccountant = normalizedRole === "accountant";

  // Campus principal keeps full campus access including fees.
  // Admin (formerly controller) now also has full campus access.
  // Administrator: campus ops without fees.
  // Accountant: fee management only for assigned campus.
  const campusOps = isSuperadmin || isPrincipal || isAdministrator || isAdmin;
  const feeOps = isSuperadmin || isPrincipal || isAccountant || isAdmin;

  return {
    isSuperadmin,
    // isAdmin now refers to the new "admin" role (former controller, campus-scoped)
    isAdmin,
    isViewer,
    isTeacher,
    isParent,
    isPrincipal,
    isAdministrator,
    isAccountant,

    canLoadStudentData: !isViewer && !isTeacher && !isParent,

    canViewCampusAnalytics: isViewer || isAdmin || isTeacher || isParent,

    canViewSystemReports: isSuperadmin || isViewer || isAdmin,

    canViewAcademicReports:
      isSuperadmin || isPrincipal || isAdministrator || isAdmin || isTeacher,

    canViewSchoolManagement:
      isSuperadmin || isPrincipal || isAdministrator || isAdmin,

    canViewFeeReports: isSuperadmin || isAdmin || isAccountant || isPrincipal,

    canViewStudents: campusOps,

    canViewFees: feeOps,

    canRegisterStudents: campusOps,

    canUpdateStudents: campusOps,

    canWithdrawStudents: campusOps,

    canDeleteStudents: isSuperadmin,

    canEditFees: isSuperadmin,

    canManageCampuses: campusOps,

    // Teacher-specific permissions
    canViewOwnTimetable: isTeacher,

    canViewAssignedStudents: isTeacher,

    canManageGrades: isTeacher,

    canViewTeacherReports: isTeacher,

    canViewCampusData: isTeacher || isViewer || isAdmin || isParent,

    canViewTeachers: campusOps,

    canViewTeacherAssignments: campusOps,

    canViewTeacherTimetable: campusOps,

    canRegisterTeachers: campusOps,

    canAssignCourses: campusOps,

    canManageTimetable: campusOps,

    // Yearly calendar — parents view only
    canViewCalendar: !isAccountant,
    canEditCalendar: isSuperadmin || isPrincipal || isAdmin || isTeacher,

    // Teacher academic tools
    canManageRemarks: isTeacher,
    canManageHomework: isTeacher,
    canManageLectures: isTeacher,

    // Parent portal (read-only)
    canViewParentPortal: isParent,
  };
}

